import React from "react";
import useYahooDspReporting from "../hooks/useYahooDspReporting";

const STATES = {
  loading: ["Loading Yahoo reporting", "Reading saved account and reporting evidence."],
  advertiser_required: ["Select an advertiser", "Choose an authorized advertiser to view Yahoo reporting."],
  disconnected: ["Yahoo is not configured", "Server configuration is required before this advertiser can connect to Yahoo."],
  unmapped: ["No Yahoo account mapping", "Map an authorized Yahoo account and campaign in Integrations."],
  mapping_required: ["Select a Yahoo account and campaign", "Reporting is scoped to the selected advertiser and mapping."],
  mapping_unavailable: ["Yahoo mapping is unavailable", "This mapping is no longer available to the selected advertiser."],
  access_unverified: ["Yahoo account access is not verified", "An authorized account read must succeed before saved reporting can be viewed."],
  reporting_unverified: ["Yahoo reporting is not verified", "No verified saved report is available for this mapping."],
  unavailable: ["Yahoo reporting is unavailable", "Saved readiness and reporting could not be refreshed."],
  empty: ["No Yahoo rows in this reporting window", "The saved report explicitly confirms zero rows. No delivery metrics are inferred."],
};
const metric = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
export const yahooClientSpend = (row) => row.spendAvailable === true && row.spendBasis === "client_billed" ? metric(row.spend) : null;
export const totalYahooMetric = (rows, getValue) => rows.length && rows.every((row) => getValue(row) !== null)
  ? rows.reduce((sum, row) => sum + getValue(row), 0) : null;
const format = (value, currency) => value === null ? "Unavailable" : currency
  ? new Intl.NumberFormat("en-US", { style: "currency", currency }).format(value)
  : value.toLocaleString("en-US");

export default function YahooDspReporting(props) {
  const { phase, status, report, mapping, mappingId, selectMapping, refresh, error } = useYahooDspReporting(props);
  const description = STATES[phase];
  const metrics = report?.rows?.length ? [
    ["Client-billed spend", totalYahooMetric(report.rows, yahooClientSpend), report.currency],
    ["Impressions", totalYahooMetric(report.rows, (row) => metric(row.impressions))],
    ["Clicks", totalYahooMetric(report.rows, (row) => metric(row.clicks))],
    ["Conversions", totalYahooMetric(report.rows, (row) => metric(row.conversions))],
  ] : [];
  return <section className="yahoo-reporting-workspace" aria-label="Yahoo saved reporting" data-yahoo-state={phase}>
    <div className="ppx-filter-row">
      {status?.mappings?.length > 0 && <label className="yahoo-reporting-label">Yahoo account and campaign
        <select className="ppx-select" aria-label="Yahoo account and campaign" value={mappingId} onChange={(event) => selectMapping(event.target.value)}>
          <option value="">Select a mapping</option>
          {status.mappings.map((item) => <option key={item.id} value={item.id}>{item.accountName || item.accountId} · campaign {item.externalCampaignId || "unmapped"}</option>)}
        </select>
      </label>}
      <button type="button" className="ppx-chip" disabled={!props.advertiserId || phase === "loading"} onClick={refresh}>Refresh saved reporting</button>
    </div>
    {description && <div className="ppx-state-panel" role={phase === "unavailable" ? "alert" : "status"} data-analytics-state={phase}>
      <h2 className="ppx-state-title">{description[0]}</h2>
      <p className="ppx-state-body">{error || description[1]}</p>
    </div>}
    {report && <>
      <div className="ppx-basis-strip yahoo-reporting-provenance" aria-label="Reporting provenance">
        <span>{report.source || "Source unavailable"}</span>
        <span>Saved {report.fetchedAt}</span>
        <span>{report.dateStart} – {report.dateEnd} · {report.timezone} · {report.currency}</span>
        <span>Yahoo seat {mapping.seatId} · advertiser {mapping.accountId} · campaign {mapping.externalCampaignId}</span>
        <span>Client-billed spend · unavailable values are not zero</span>
      </div>
      {metrics.length > 0 && <div className="ppx-kpi-strip" aria-label="Yahoo reporting totals">
        {metrics.map(([label, value, currency]) => <div className="ppx-kpi-card" key={label}>
          <span className="ppx-kpi-label">{label}</span><strong className="ppx-kpi-value">{format(value, currency)}</strong>
        </div>)}
      </div>}
      {report.rows.length > 0 && <div className="ppx-panel yahoo-reporting-table-scroll" tabIndex={0} role="region" aria-label="Yahoo daily reporting table">
        <table className="yahoo-reporting-table"><caption>Saved Yahoo daily performance · {report.currency}</caption>
          <thead><tr><th scope="col">Source date</th><th scope="col">Impressions</th><th scope="col">Clicks</th><th scope="col">Conversions</th><th scope="col">Client-billed spend</th></tr></thead>
          <tbody>{report.rows.map((row) => <tr key={row.date}><th scope="row">{row.date}</th><td>{format(metric(row.impressions))}</td><td>{format(metric(row.clicks))}</td><td>{format(metric(row.conversions))}</td><td>{format(yahooClientSpend(row), report.currency)}</td></tr>)}</tbody>
        </table>
      </div>}
      <p className="ppx-control-note">This is saved reporting, not a live Yahoo request. {report.recentDataLagHours ? `Recent provider data may lag ${report.recentDataLagHours} hours.` : "Provider freshness is not declared."}</p>
    </>}
  </section>;
}
